// =============================================================================
// Partner shell — clones Client Dashboard layout (Sidebar / Topbar / PageSection)
// =============================================================================

import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "@veorest/api-client";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sidebar,
  Topbar,
  PageSection,
  type SidebarSection,
} from "@veorest/ui";
import {
  LayoutDashboard,
  ShoppingBag,
  Bike,
  Store,
  BarChart3,
  Wallet,
  Settings,
  Menu,
  ChevronDown,
  LogOut,
  Bell,
  X,
} from "lucide-react";
import { ROUTES, isAppPath, isClientDetailPath, isDriverDetailPath } from "../routes";
import { OeloxLogo } from "../components/OeloxLogo";
import { usePartnerSessionStore } from "../lib/partnerAuth";
import { useI18n } from "../i18n";
import PartnerHomePage from "./PartnerHomePage";
import OrdersPage from "./OrdersPage";
import DriversPage from "./DriversPage";
import DriverViewPage from "./DriverViewPage";
import ClientsPage from "./ClientsPage";
import ClientViewPage from "./ClientViewPage";
import AnalyticsPage from "./AnalyticsPage";
import WalletPage from "./WalletPage";
import SettingsPage from "./SettingsPage";

type NavKey =
  | "dashboard"
  | "orders"
  | "drivers"
  | "clients"
  | "analytics"
  | "wallet"
  | "settings";

const PATH_TO_NAV_KEY: Record<string, NavKey> = {
  [ROUTES.DASHBOARD]: "dashboard",
  [ROUTES.ORDERS]: "orders",
  [ROUTES.DRIVERS]: "drivers",
  [ROUTES.CLIENTS]: "clients",
  [ROUTES.ANALYTICS]: "analytics",
  [ROUTES.WALLET]: "wallet",
  [ROUTES.SETTINGS]: "settings",
};

function resolveNavKey(pathname: string): NavKey {
  if (isClientDetailPath(pathname)) return "clients";
  if (isDriverDetailPath(pathname)) return "drivers";
  return PATH_TO_NAV_KEY[pathname] ?? "dashboard";
}

export default function DashboardPage() {
  const { t, direction } = useI18n();
  const isRtl = direction === "rtl";
  const { user, signOut } = useAuthStore();
  const partnerSession = usePartnerSessionStore((s) => s.session);
  const clearPartner = usePartnerSessionStore((s) => s.clear);
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setProfileDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isAppPath(location.pathname)) {
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
  }, [location.pathname, navigate]);

  const currentKey = resolveNavKey(location.pathname);
  const navIconClass = "w-5 h-5";
  const fallbackName = t("shell.workspaceFallback");

  const sidebarSections: SidebarSection[] = [
    {
      items: [
        {
          key: "dashboard",
          label: t("nav.dashboard"),
          icon: <LayoutDashboard className={navIconClass} />,
          onClick: () => navigate(ROUTES.DASHBOARD),
        },
        {
          key: "orders",
          label: t("nav.orders"),
          icon: <ShoppingBag className={navIconClass} />,
          onClick: () => navigate(ROUTES.ORDERS),
        },
        {
          key: "drivers",
          label: t("nav.drivers"),
          icon: <Bike className={navIconClass} />,
          onClick: () => navigate(ROUTES.DRIVERS),
        },
        {
          key: "clients",
          label: t("nav.clients"),
          icon: <Store className={navIconClass} />,
          onClick: () => navigate(ROUTES.CLIENTS),
        },
        {
          key: "analytics",
          label: t("nav.analytics"),
          icon: <BarChart3 className={navIconClass} />,
          onClick: () => navigate(ROUTES.ANALYTICS),
        },
        {
          key: "wallet",
          label: t("nav.wallet"),
          icon: <Wallet className={navIconClass} />,
          onClick: () => navigate(ROUTES.WALLET),
        },
        {
          key: "settings",
          label: t("nav.settings"),
          icon: <Settings className={navIconClass} />,
          onClick: () => navigate(ROUTES.SETTINGS),
        },
      ],
    },
  ];

  const handleSignOut = async () => {
    clearPartner();
    await signOut();
    navigate(ROUTES.LOGIN);
  };

  const renderSubPage = () => {
    if (isClientDetailPath(location.pathname)) {
      return <ClientViewPage />;
    }
    if (isDriverDetailPath(location.pathname)) {
      return <DriverViewPage />;
    }
    switch (currentKey) {
      case "dashboard":
        return <PartnerHomePage />;
      case "orders":
        return <OrdersPage />;
      case "drivers":
        return <DriversPage />;
      case "clients":
        return <ClientsPage />;
      case "analytics":
        return <AnalyticsPage />;
      case "wallet":
        return <WalletPage />;
      case "settings":
        return <SettingsPage />;
      default:
        return null;
    }
  };

  const userDisplayName =
    partnerSession?.displayName ||
    user?.email?.split("@")[0] ||
    fallbackName;
  const userInitials = userDisplayName.substring(0, 2).toUpperCase();
  const businessName = partnerSession?.partnerName || fallbackName;

  return (
    <div className="partner-shell bg-[var(--surface-muted)]">
      <Topbar
        variant="brand"
        className="partner-topbar px-3 md:px-4 flex items-center gap-2"
      >
        <button
          className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-white/90 hover:text-white hover:bg-white/15 transition-colors md:hidden"
          onClick={() => setMobileMenuOpen(true)}
          aria-label={t("shell.openMenu")}
        >
          <Menu className="w-5 h-5" />
        </button>

        <div className="flex items-center min-w-0">
          <OeloxLogo variant="header" />
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-1">
          <button
            type="button"
            className="oelox-topbar-chip partner-topbar__icon-btn"
            aria-label={t("shell.notifications")}
          >
            <Bell className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={() => navigate(ROUTES.SETTINGS)}
            className="oelox-topbar-chip partner-topbar__icon-btn"
            aria-label={t("shell.settings")}
          >
            <Settings className="w-4 h-4" />
          </button>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              aria-haspopup="true"
              aria-expanded={profileDropdownOpen}
              className="oelox-topbar-chip partner-topbar__avatar-btn cursor-pointer"
            >
              <div className="w-7 h-7 rounded-full bg-white text-[var(--accent)] font-black text-[11px] flex items-center justify-center flex-shrink-0">
                {userInitials}
              </div>
              <ChevronDown
                className={`w-3.5 h-3.5 text-white/75 transition-transform ${
                  profileDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {profileDropdownOpen ? (
              <div
                className={`absolute top-[calc(100%+6px)] w-56 z-50 animate-fadeIn ${
                  isRtl ? "left-0" : "right-0"
                }`}
              >
                <div className="partner-topbar__menu bg-white border border-[var(--border)] overflow-hidden">
                  <div className="px-3.5 py-3 border-b border-[var(--border)]">
                    <p className="text-sm font-bold text-[var(--text-primary)] truncate">
                      {userDisplayName}
                    </p>
                    <p className="text-[11px] text-[var(--text-muted)] truncate">
                      {user?.email}
                    </p>
                    <p className="mt-0.5 text-[11px] font-semibold text-[var(--text-secondary)] truncate">
                      {businessName}
                    </p>
                  </div>
                  <div className="p-1">
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-semibold text-red-500 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <LogOut className="w-4 h-4" /> {t("shell.signOut")}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </Topbar>

      <Sidebar
        sections={sidebarSections}
        activeKey={currentKey}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onSignOut={handleSignOut}
        signOutLabel={t("shell.signOut")}
        workspaceLabel={businessName}
        side={isRtl ? "right" : "left"}
      />

      <div
        style={{ paddingTop: "var(--header-height)" }}
        className={`partner-shell__content min-h-0 transition-all duration-300 ease-in-out ${
          sidebarCollapsed
            ? isRtl
              ? "md:pr-[var(--sidebar-collapsed)]"
              : "md:pl-[var(--sidebar-collapsed)]"
            : isRtl
              ? "md:pr-[var(--sidebar-width)]"
              : "md:pl-[var(--sidebar-width)]"
        }`}
      >
        <PageSection
          className={`flex-1 animate-slide-up ${
            (currentKey === "orders" || currentKey === "clients") &&
            !isClientDetailPath(location.pathname)
              ? "page-section--workspace"
              : ""
          }`}
        >
          {renderSubPage()}
        </PageSection>
      </div>

      {mobileMenuOpen ? (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden animate-fadeIn"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div
            className={`oelox-sidebar fixed top-0 bottom-0 w-[var(--sidebar-width)] z-50 md:hidden animate-slide-up overflow-y-auto flex flex-col ${
              isRtl ? "right-0" : "left-0"
            }`}
          >
            <div
              className="flex items-center justify-between h-[var(--header-height)] px-4 shrink-0"
              style={{ background: "var(--accent)" }}
            >
              <OeloxLogo variant="header" />
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/90 hover:bg-white/15 transition-all"
                aria-label={t("common.close")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <nav className="flex-1 py-3 px-2">
              {sidebarSections.map((section, i) => (
                <div key={i}>
                  {section.items.map((item) => {
                    const isActive = currentKey === item.key;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => {
                          item.onClick();
                          setMobileMenuOpen(false);
                        }}
                        className={`oelox-sidebar__item oelox-sidebar__item--expanded mb-0.5 ${
                          isActive ? "oelox-sidebar__item--active" : ""
                        }`}
                      >
                        <span className="oelox-sidebar__item-icon">{item.icon}</span>
                        <span className="oelox-sidebar__item-label">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </nav>

            <div className="oelox-sidebar__footer">
              <button
                type="button"
                onClick={() => {
                  void handleSignOut();
                  setMobileMenuOpen(false);
                }}
                className="oelox-sidebar__signout oelox-sidebar__signout--expanded"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-[13px]">{t("shell.signOut")}</span>
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
